import RecordView from "@/components/record/RecordView";
import { Button } from "@/components/ui/button";
import { Module, PATH_PROJECTS } from "@/constants";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { CalendarIcon } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { ProjectMenu } from "./menu/ProjectMenu";

interface ProjectDetailsAndNavigationProps {
  projectId?: string;
  isOpen?: boolean;
  showEditButton?: boolean;
  showMenu?: boolean;
}

const ProjectDetailsAndNavigationComponent = ({
  projectId: projectIdFormProps,
  isOpen = false,
  showEditButton = true,
  showMenu = true,
}: ProjectDetailsAndNavigationProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const projectIdFromRoute = useProjectId();
  const projectId = projectIdFormProps || projectIdFromRoute;
  const query = useProjectQuery(projectId);
  const { can_update } = useModulePermissions(Module.Projects);

  const onProjectEdit = useCallback(() => {
    navigate(`${PATH_PROJECTS}/${projectId}`);
  }, [navigate, projectId]);

  if (!projectId) {
    return null;
  }

  return (
    <section className="flex flex-col gap-2">
      {showMenu && projectId && <ProjectMenu projectId={projectId} />}
      <RecordView
        query={query}
        translationKey={Module.Projects}
        isOpen={isOpen}
        header={(data) => (
          <span className="flex flex-row gap-4 items-center">
            {data.name}{" "}
            <span className="flex flex-row gap-1 items-center text-muted-foreground">
              <RecordView.Field
                name="start_date"
                label={<CalendarIcon className="w-4 h-4" />}
                className="text-xs inline-flex flex-row gap-1 items-center text-muted-foreground"
              />
              <RecordView.Field
                name="end_date"
                label="–"
                className="text-xs inline-flex flex-row gap-1 items-center text-muted-foreground"
              />
            </span>
          </span>
        )}
        actions={[
          showEditButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={onProjectEdit}
              key="edit"
              disabled={!can_update}
            >
              {t("projects.recordView.edit")}
            </Button>
          ),
        ]}
      >
        <RecordView.Field name="ref_number" />
        <RecordView.Field name="project_type" />
        <RecordView.Field name="client" />
        <RecordView.Field name="primary_manager" />
        <RecordView.Field
          name="start_date"
          icon={<CalendarIcon className="w-4 h-4" />}
        />
        <RecordView.Field
          name="end_date"
          icon={<CalendarIcon className="w-4 h-4" />}
        />
        <RecordView.Field
          name="status"
          renderer={(value) => t(`projectStatuses.${value}`)}
        />
      </RecordView>
    </section>
  );
};

export const ProjectDetailsAndNavigation = memo(
  ProjectDetailsAndNavigationComponent,
);
